import { useEffect, useState } from "react";
import { Banner, Card, CardHead, Empty, Label, Num, Pill, Skeleton, Toasts, useToasts } from "../components/ui";
import { IconPricing } from "../components/icons";

interface MarkupConfig {
  default_bps: number;
  client_overrides: { client_id: string; bps: number; note: string | null }[];
  pair_rules: { asset_a: string; asset_b: string; bps: number; note: string | null }[];
}

const auth = () => ({
  authorization: `Bearer ${sessionStorage.getItem("otc.session") ?? ""}`,
  "content-type": "application/json",
});

export function Pricing() {
  const [config, setConfig] = useState<MarkupConfig | null>(null);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const toast = useToasts();

  async function load() {
    const res = await fetch("/api/pricing/markup", { headers: auth() });
    if (res.ok) {
      const c = (await res.json()) as MarkupConfig;
      setConfig(c);
      setDraft(String(c.default_bps));
    } else setError("Could not load pricing config.");
  }

  useEffect(() => {
    void load();
  }, []);

  const bps = Number(draft);
  const valid = Number.isInteger(bps) && bps >= 0 && bps <= 10_000;
  const dirty = config !== null && String(config.default_bps) !== draft;

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!valid) return;
    setBusy(true);
    const res = await fetch("/api/pricing/markup/default", {
      method: "PUT",
      headers: auth(),
      body: JSON.stringify({ bps }),
    });
    if (res.ok) {
      toast.push("ok", `Default markup is now ${bps} bps (${(bps / 100).toFixed(2)}%). Change recorded in the audit log.`);
      await load();
    } else {
      toast.push("error", "Save failed. The value must be a whole number between 0 and 10000.");
    }
    setBusy(false);
  }

  if (!config && !error) return <Skeleton rows={4} />;
  if (error) return <Banner tone="error">{error}</Banner>;
  if (!config) return null;

  return (
    <>
      <p className="page-intro">
        The desk's markup, and the two things that override it. Precedence runs pair rule → client override →
        default, so a pair exemption beats everything.
      </p>

      <Card pad={false} className="enter" >
        <CardHead title="Default markup" right={<Pill tone="info">applies to everyone</Pill>} />
        <form className="card-pad" onSubmit={save} style={{ maxWidth: 460 }}>
          <div className="field" style={{ marginBottom: 0 }}>
            <label htmlFor="bps">Basis points</label>
            <div className="row">
              <input
                id="bps"
                inputMode="numeric"
                className="num"
                value={draft}
                aria-invalid={!valid}
                aria-describedby="bps-hint"
                onChange={(e) => setDraft(e.target.value)}
              />
              <div className="shrink">
                <button className="btn btn-primary" disabled={!valid || !dirty || busy}>
                  {busy ? "Saving" : "Save"}
                </button>
              </div>
            </div>
            {/* The percentage is shown live rather than after saving: bps is the
                unit the desk configures in, but percent is the unit anyone
                sanity-checks in, and a typo of 400 for 40 is a 10x pricing
                error that reads as harmless in bps alone. */}
            <div className="field-hint" id="bps-hint">
              {valid
                ? <>= <strong className="num" style={{ color: "var(--text)" }}>{(bps / 100).toFixed(2)}%</strong> on the venue leg. A 25,000 EUR buy earns <span className="num">{((25_000 * bps) / 10_000 / (1 + bps / 10_000)).toFixed(2)}</span> EUR.</>
                : <span className="loss">Must be a whole number between 0 and 10000.</span>}
            </div>
          </div>
        </form>
      </Card>

      <div className="section">
        <Label>Pair exemptions</Label>
        <p className="page-intro" style={{ margin: "6px 0 12px" }}>
          Override everything, for everyone. Used where a pair carries no spread worth charging for.
        </p>
        <Card pad={false}>
          {config.pair_rules.length === 0 ? (
            <Empty icon={<IconPricing size={20} />} title="No pair exemptions" body="Every pair is priced at the default." />
          ) : (
            <div className="table-wrap flush">
              <table>
                <thead><tr><th>Pair</th><th className="right">Markup</th><th>Note</th></tr></thead>
                <tbody>
                  {config.pair_rules.map((r) => (
                    <tr key={`${r.asset_a}/${r.asset_b}`}>
                      <td style={{ fontWeight: 500 }}>{r.asset_a} <span className="dimmer">↔</span> {r.asset_b}</td>
                      <td className="right num">
                        {r.bps === 0 ? <Pill tone="good">zero</Pill> : <><Num value={r.bps} /> bps</>}
                      </td>
                      <td className="dim">{r.note ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <div className="section">
        <Label>Client overrides</Label>
        <p className="page-intro" style={{ margin: "6px 0 12px" }}>
          Per-counterparty pricing. Beats the default, loses to a pair exemption.
        </p>
        <Card pad={false}>
          {config.client_overrides.length === 0 ? (
            <Empty
              icon={<IconPricing size={20} />}
              title="No client overrides"
              body="Every counterparty is on the default markup. Client records arrive with the Xchange360 sync, after which they can be given their own rate here."
            />
          ) : (
            <div className="table-wrap flush">
              <table>
                <thead><tr><th>Client</th><th className="right">Markup</th><th>Note</th></tr></thead>
                <tbody>
                  {config.client_overrides.map((o) => (
                    <tr key={o.client_id}>
                      <td className="mono">{o.client_id}</td>
                      <td className="right num">
                        {o.bps === 0 ? <Pill tone="good">zero</Pill> : <><Num value={o.bps} /> bps</>}
                      </td>
                      <td className="dim">{o.note ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <Toasts items={toast.items} onDone={toast.dismiss} />
    </>
  );
}
